import { mergeWith, pick, flow, isBoolean } from 'lodash';
import voca from 'voca';
import { widgetParams } from '@cld/upload-widget-common';
import { isSupported as isEncryptSupported } from 'utils/encryption';
import defaults, { getAllSources } from './common/defaults';
import { stringifyMetadata } from './common/utils';

const NO_MERGE_CONFIG_ARGS = ['sources', 'tags', 'fonts']; // array type params that shouldnt be merged but always use the newest value from the client

export const configMerger = (...configs) =>
  mergeWith(...configs, (objValue, srcValue, key) => {
    if (~NO_MERGE_CONFIG_ARGS.indexOf(key)) {
      // stop lodash from merging the specific config arrays
      return srcValue;
    }
  });

const getWithValidSources = (config) => {
  const configSources = config.sources;
  const allSources = getAllSources();

  if (!Array.isArray(configSources) || !configSources || !configSources.length) {
    config.sources = allSources;
  } else {
    config.sources = configSources.filter((src) => !!~allSources.indexOf(src));
  }

  return config;
};

const getWithMaxFilesConfig = (config) => {
  const maxFiles = config.maxFiles ? parseInt(config.maxFiles) : null;

  config.maxFiles = maxFiles && !isNaN(maxFiles) ? maxFiles : null;

  return config;
};

const getWithCroppingConfig = (config) => {
  if (config.cropping && config.cropping !== 'none') {
    config.cropping = true;
    config.multiple = false;
  } else {
    config.cropping = null;
    config.multiple = config.maxFiles === 1 ? false : config.multiple;
  }

  return config;
};

const getWithValidSearchSites = (config) => {
  if (voca.isString(config.searchBySites)) {
    config.searchBySites = [config.searchBySites];
  }

  return config;
};

const getWithDefaultTruthParams = (config) => {
  if (!isBoolean(config.showUploadMoreButton)) {
    config.showUploadMoreButton = true;
  }

  if (!isBoolean(config.singleUploadAutoClose)) {
    config.singleUploadAutoClose = true;
  }

  if (!isBoolean(config.showSkipCropButton)) {
    config.showSkipCropButton = true;
  }

  return config;
};

const getWithEncryption = (config) => {
  if (config.encryption && isEncryptSupported) {
    config.cropping = undefined; // cropping isnt possible when encryption is enabled
    config.resourceType = 'raw'; // force raw uploads
  } else {
    config.encryption = undefined;
  }

  return config;
};

const getValidContext = (config) => {
  if (config.context) {
    config.context = stringifyMetadata(config.context);
  }

  return config;
};

const configValidator = flow([
  getWithMaxFilesConfig,
  getWithEncryption, // needs to run before cropping check
  getWithCroppingConfig,
  getWithValidSources,
  getWithValidSearchSites,
  getWithDefaultTruthParams,
  getValidContext,
]);

export default (options) => {
  const allowedParams = [].concat(
    widgetParams.MANDATORY_PARAMS,
    widgetParams.BEHAVIOR_PARAMS,
    widgetParams.UPLOAD_PARAMS,
    widgetParams.CLIENT_SIDE_PARAMS,
    widgetParams.ADVANCED_PARAMS,
    widgetParams.CUSTOMIZATION_PARAMS,
    widgetParams.INTERNAL_PARAMS,
    widgetParams.THIRD_PARTY_PARAMS,
    widgetParams.CROP_PARAMS
  );

  options = pick(options, allowedParams);

  const config = configMerger({}, defaults, options);

  return configValidator(config);
};
